import {
  Children,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';

type Breakpoint = 'md' | 'lg' | 'xl';

interface Props {
  children: ReactNode;
  defaultSizes: number[];
  minSizes?: number[];
  breakpoint?: Breakpoint;
  className?: string;
}

const BREAKPOINTS: Record<Breakpoint, number> = {
  md: 768,
  lg: 1024,
  xl: 1280,
};

function sizesForCount(values: number[], count: number) {
  if (values.length === count && values.every((value) => value > 0)) {
    return values;
  }
  return Array.from({ length: count }, () => 1);
}

function clampPair(
  sizes: number[],
  index: number,
  delta: number,
  minSizes: number[]
) {
  const next = [...sizes];
  const pairTotal = sizes[index] + sizes[index + 1];
  const minLeft = minSizes[index] ?? 1;
  const minRight = minSizes[index + 1] ?? 1;
  const left = Math.min(
    Math.max(sizes[index] + delta, minLeft),
    pairTotal - minRight
  );

  next[index] = left;
  next[index + 1] = pairTotal - left;
  return next;
}

function useIsResizable(breakpoint: Breakpoint) {
  const query = `(min-width: ${BREAKPOINTS[breakpoint]}px)`;
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const handleChange = () => setMatches(media.matches);

    handleChange();
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, [query]);

  return matches;
}

export default function ResizableColumns({
  children,
  defaultSizes,
  minSizes,
  breakpoint = 'lg',
  className,
}: Props) {
  const panes = Children.toArray(children);
  const containerRef = useRef<HTMLDivElement>(null);
  const defaultSizeKey = defaultSizes.join('|');
  const minSizeKey = (minSizes ?? []).join('|');
  const resolvedDefaultSizes = useMemo(
    () => sizesForCount(defaultSizes, panes.length),
    [defaultSizeKey, panes.length]
  );
  const resolvedMinSizes = useMemo(
    () => sizesForCount(minSizes ?? [], panes.length),
    [minSizeKey, panes.length]
  );
  const [sizes, setSizes] = useState(resolvedDefaultSizes);
  const isResizable = useIsResizable(breakpoint);

  useEffect(() => {
    setSizes(resolvedDefaultSizes);
  }, [resolvedDefaultSizes]);

  const adjustHandle = (index: number, delta: number) => {
    setSizes((current) => clampPair(current, index, delta, resolvedMinSizes));
  };

  const startResize = (event: PointerEvent<HTMLButtonElement>, index: number) => {
    if (!containerRef.current) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);

    const startX = event.clientX;
    const startSizes = [...sizes];
    const containerWidth = containerRef.current.getBoundingClientRect().width;
    if (containerWidth <= 0) return;

    const totalSize = startSizes.reduce((sum, size) => sum + size, 0);
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handleMove = (moveEvent: globalThis.PointerEvent) => {
      const delta = ((moveEvent.clientX - startX) / containerWidth) * totalSize;
      setSizes(clampPair(startSizes, index, delta, resolvedMinSizes));
    };

    const stopResize = () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', stopResize);
      window.removeEventListener('pointercancel', stopResize);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', stopResize);
    window.addEventListener('pointercancel', stopResize);
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      adjustHandle(index, -2);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      adjustHandle(index, 2);
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      setSizes(resolvedDefaultSizes);
    }
  };

  if (!isResizable || panes.length < 2) {
    return (
      <div className={`grid grid-cols-1 gap-4 ${className ?? ''}`}>
        {panes.map((pane, index) => (
          <div key={index} className="min-w-0">
            {pane}
          </div>
        ))}
      </div>
    );
  }

  const gridTemplateColumns = sizes
    .map((size, index) =>
      index === sizes.length - 1
        ? `minmax(0, ${size}fr)`
        : `minmax(0, ${size}fr) 1rem`
    )
    .join(' ');

  return (
    <div
      ref={containerRef}
      className={`grid items-start ${className ?? ''}`}
      style={{ gridTemplateColumns }}
    >
      {panes.map((pane, index) => (
        <Fragment key={index}>
          <div key={`pane-${index}`} className="min-w-0">
            {pane}
          </div>
          {index < panes.length - 1 && (
            <button
              key={`handle-${index}`}
              type="button"
              className="group flex h-full min-h-24 cursor-col-resize items-stretch justify-center self-stretch rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
              title="Drag to resize panes. Double-click to reset."
              aria-label="Resize adjacent panes"
              onPointerDown={(event) => startResize(event, index)}
              onDoubleClick={() => setSizes(resolvedDefaultSizes)}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              <span className="my-2 w-px rounded-full bg-slate-300 transition-all group-hover:w-1 group-hover:bg-accent dark:bg-navy-700 dark:group-hover:bg-accent" />
            </button>
          )}
        </Fragment>
      ))}
    </div>
  );
}
